import { CharacterAvatar } from "@/components/character/CharacterAvatar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCharacter } from "@/hooks/useCharacter";
import type { Character } from "@/types/character";
import { ArrowLeft, MessageSquareShare, Star } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

function useShare(character: Character | null | undefined, characterId: string | undefined) {
  const [shared, setShared] = useState(false);
  const shareUrl = `${window.location.origin}/chat/${characterId}`;
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Sylvain - ${character?.name}`,
          text: `Je parle avec ${character?.name} sur Sylvain et tu devrais aussi !`,
          url: shareUrl,
        });
        setShared(true);
      } catch {
        // user cancelled or error
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShared(true);
        toast.success("Lien copié !");
      } catch {
        // fallback: do nothing
      }
    }
    setTimeout(() => setShared(false), 2000);
  };
  return { handleShare, shared };
}

const ChatDetails = () => {
  const { characterId } = useParams();
  const { data: character, isLoading, error } = useCharacter(characterId || "");
  const { handleShare, shared } = useShare(character, characterId);

  return (
    <div className="gradient-bg flex min-h-full flex-col items-center justify-start px-4">
      <div className="mx-auto w-full max-w-lg">
        {/* Header with back button and title */}
        <div className="h-18 animate-slide-in-from-right flex items-center gap-4">
          <Button variant="ghost" className="p-2" asChild aria-label="Retour">
            <Link to={`/chat/${characterId}`}>
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <h1 className="text-lg font-semibold text-gray-700">
            Détails de la conversation
          </h1>
        </div>
        <div className="flex w-full flex-col items-center gap-6">
          {isLoading || !character ?
            <Card className="backdrop-blur-xs w-full max-w-md border-0 bg-white/90 shadow-lg">
              <CardHeader className="pb-1 text-center">
                <Skeleton className="mx-auto mb-4 h-24 w-24 rounded-full" />
                <Skeleton className="mx-auto mb-2 h-8 w-40" />
                <Skeleton className="mx-auto mb-2 h-6 w-32" />
                <Skeleton className="mx-auto mb-4 h-5 w-60" />
                <Skeleton className="mx-auto mb-2 h-5 w-48" />
              </CardHeader>
            </Card>
          : error ?
            <div className="text-red-600">
              Erreur lors du chargement du personnage.
            </div>
          : <Card className="backdrop-blur-xs animate-fade-in w-full max-w-md border-0 bg-white/90 shadow-lg">
              <CardHeader className="pb-1 text-center">
                <div className="relative mx-auto mb-3">
                  <CharacterAvatar
                    name={character.name}
                    avatar_url={character.avatar_url ?? undefined}
                    className="h-42 w-42 mx-auto"
                  />
                </div>
                <div className="mb-2 flex items-center justify-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${i < Math.floor(character.rating) ? "text-sylvain-blue fill-current" : "text-gray-300"}`}
                      fill={
                        i < Math.floor(character.rating) ?
                          "currentColor"
                        : "none"
                      }
                    />
                  ))}
                  <span className="ml-1 text-sm text-gray-600">
                    {character.rating}
                  </span>
                </div>
                <CardTitle className="mb-1 text-lg">{character.name}</CardTitle>
                <CardDescription className="text-sylvain-blue font-medium">
                  {character.title}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-1">
                {character.short_description && (
                  <p className="text-center text-sm leading-relaxed text-gray-600">
                    {character.short_description}
                  </p>
                )}
                {character.tags.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1">
                    {character.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="bg-linear-to-r rounded-full border border-blue-200 from-blue-50 to-indigo-50 px-2 py-1 text-sm font-medium text-blue-700"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}
                {character.first_message_helper_text && (
                  <div className="mt-4 leading-snug">
                    {character.first_message_helper_text}
                  </div>
                )}
              </CardContent>
            </Card>
          }
          <Button
            onClick={handleShare}
            className="text-primary h-16 w-full shadow-md"
            variant="secondary"
            size="xl"
            aria-label="Partager le personnage"
          >
            <MessageSquareShare className="h-6 w-6" />
            {shared ? "Lien copié !" : `Partager ${character?.name}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatDetails;
